import { PopupButtonType, PopupIconType, showPopup } from "@kalyzee/kast-app-web-components";
import { useEffect, useState } from "react";
import { useStore } from "react-redux";
import PageContainer from "../../components/page/PageContainer";
import { ApiError } from "../../helpers/apiError";
import { request } from "../../helpers/request";
import { toastError, toastSuccess } from "../../helpers/toast";
import {
  API_KEY_DEFAULT_RATE_LIMIT_PER_MINUTE,
  API_KEY_PERMISSION_DESCRIPTIONS,
  ApiKey,
  ApiKeyPayload,
  ApiKeyPermission,
  CreatedApiKey,
} from "../../interfaces/apiKey";
import styles from "./api-keys.module.css";

// API keys live in the middleware's database, shared by every server: one list, on the main api.
// The secret is only returned by the creation — it is shown once, in a popup, then lost.

interface Draft {
  id?: string;
  name: string;
  permissions: ApiKeyPermission[];
  filterIps: string; // one pattern per line (or comma separated)
  expiresAt: string; // datetime-local value, "" → no expiry
  rateLimit: string; // "" → middleware default on creation, unchanged on update
  unlimited: boolean;
}

const emptyDraft = (): Draft => ({
  name: "",
  permissions: [ApiKeyPermission.STATUS_READ],
  filterIps: "",
  expiresAt: "",
  rateLimit: "",
  unlimited: false,
});

const toDatetimeLocal = (iso?: string | null) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const draftFromApiKey = (apiKey: ApiKey): Draft => ({
  id: apiKey._id,
  name: apiKey.name,
  permissions: [...(apiKey.permissions ?? [])],
  filterIps: (apiKey.filterIps ?? []).join("\n"),
  expiresAt: toDatetimeLocal(apiKey.expiresAt),
  rateLimit: typeof apiKey.rateLimitPerMinute === "number" ? String(apiKey.rateLimitPerMinute) : "",
  unlimited: apiKey.rateLimitPerMinute === null,
});

const draftToPayload = (draft: Draft): ApiKeyPayload => {
  const payload: ApiKeyPayload = {
    name: draft.name.trim(),
    permissions: draft.permissions,
    filterIps: draft.filterIps
      .split(/[\n,]/)
      .map((ip) => ip.trim())
      .filter((ip) => ip.length),
  };
  if (draft.expiresAt) payload.expiresAt = new Date(draft.expiresAt).toISOString();
  else if (draft.id) payload.expiresAt = null;
  if (draft.unlimited) payload.rateLimitPerMinute = null;
  else if (draft.rateLimit) payload.rateLimitPerMinute = Number(draft.rateLimit);
  return payload;
};

const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : "-");

const isExpired = (apiKey: ApiKey) => !!apiKey.expiresAt && new Date(apiKey.expiresAt).getTime() <= Date.now();

const ApiKeysPage = () => {
  const store = useStore();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState<Draft>();
  const [busy, setBusy] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);

  const call = async <T,>(url: string, method: string, body?: object): Promise<T | undefined> => {
    try {
      return await request(store, url, method, body, true, false);
    } catch (err: any) {
      toastError(ApiError.isApiError(err) ? err.message : `${err?.message ?? err}`);
      return undefined;
    }
  };

  const refresh = async () => {
    const result = await call<ApiKey[]>("/apikey?limit=200", "get");
    if (result) setApiKeys(result);
    setLoaded(true);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDraftField = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const togglePermission = (permission: ApiKeyPermission, checked: boolean) => {
    setDraft((current) => {
      if (!current) return current;
      const permissions = current.permissions.filter((p) => p !== permission);
      if (checked) permissions.push(permission);
      return { ...current, permissions };
    });
  };

  const copySecret = (secret: string) => {
    navigator.clipboard
      .writeText(secret)
      .then(() => toastSuccess("Clé copiée" /* TRANSLATION */))
      .catch(() => toastError("Copie impossible" /* TRANSLATION */));
  };

  const showSecret = (created: CreatedApiKey) => {
    showPopup({
      title: `API key "${created.name}"`,
      iconTitle: PopupIconType.WARNING,
      content: (
        <div className={styles.popupContent}>
          <div className={styles.warnBanner}>
            {"Copiez la clé maintenant : elle ne pourra plus être affichée. Elle s'envoie dans le header x-api-key." /* TRANSLATION */}
          </div>
          <div className={styles.secret}>{created.secret}</div>
          <button type="button" className={styles.actionButton} onClick={() => copySecret(created.secret)}>
            {"Copier" /* TRANSLATION */}
          </button>
        </div>
      ),
      buttons: [{ type: PopupButtonType.OK, element: "OK" }],
    });
  };

  const save = async () => {
    if (!draft) return;
    const payload = draftToPayload(draft);
    if (!payload.name) {
      toastError("Le nom est requis" /* TRANSLATION */);
      return;
    }
    if (!payload.permissions.length) {
      toastError("Au moins une permission est requise" /* TRANSLATION */);
      return;
    }
    if (payload.rateLimitPerMinute !== undefined && payload.rateLimitPerMinute !== null && !(Number.isInteger(payload.rateLimitPerMinute) && payload.rateLimitPerMinute >= 1)) {
      toastError("La limite doit être un entier ≥ 1" /* TRANSLATION */);
      return;
    }
    setBusy(true);
    if (draft.id) {
      const updated = await call<ApiKey>(`/apikey/${draft.id}`, "PATCH", payload);
      if (updated) {
        toastSuccess("API key mise à jour" /* TRANSLATION */);
        setDraft(undefined);
      }
    } else {
      const created = await call<CreatedApiKey>("/apikey", "POST", payload);
      if (created) {
        setDraft(undefined);
        showSecret(created);
      }
    }
    await refresh();
    setBusy(false);
  };

  const confirmRevoke = (apiKey: ApiKey) => {
    showPopup({
      title: "Révoquer l'API key" /* TRANSLATION */,
      iconTitle: PopupIconType.WARNING,
      content: `Révoquer "${apiKey.name}" ? Les appels utilisant cette clé seront refusés. Cette action est définitive.` /* TRANSLATION */,
      buttons: [
        { type: PopupButtonType.CANCEL, element: "Non" },
        {
          type: PopupButtonType.VALIDATE,
          element: "Oui",
          onClick: async () => {
            setBusy(true);
            const revoked = await call<ApiKey>(`/apikey/${apiKey._id}/revoke`, "PATCH");
            if (revoked) toastSuccess("API key révoquée" /* TRANSLATION */);
            if (draft?.id === apiKey._id) setDraft(undefined);
            await refresh();
            setBusy(false);
            return true;
          },
        },
      ],
    });
  };

  const renderEditor = () => {
    if (!draft) return null;
    return (
      <div className={styles.editor}>
        <div className={styles.fieldGrid}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{"Nom" /* TRANSLATION */}</span>
            <input
              className={styles.input}
              value={draft.name}
              placeholder="Supervision kast"
              onChange={(event) => setDraftField("name", event.currentTarget.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{"Expiration (vide = jamais)" /* TRANSLATION */}</span>
            <input
              className={styles.input}
              type="datetime-local"
              value={draft.expiresAt}
              onChange={(event) => setDraftField("expiresAt", event.currentTarget.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {`Requêtes / minute${draft.id ? "" : ` (vide = ${API_KEY_DEFAULT_RATE_LIMIT_PER_MINUTE})`}` /* TRANSLATION */}
            </span>
            <input
              className={styles.input}
              type="number"
              min={1}
              disabled={draft.unlimited}
              value={draft.rateLimit}
              onChange={(event) => setDraftField("rateLimit", event.currentTarget.value)}
            />
          </label>
          <label className={styles.fieldInline}>
            <input type="checkbox" checked={draft.unlimited} onChange={(event) => setDraftField("unlimited", event.currentTarget.checked)} />
            {"Sans limite" /* TRANSLATION */}
          </label>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>{"Permissions" /* TRANSLATION */}</div>
          {Object.values(ApiKeyPermission).map((permission) => (
            <label key={permission} className={styles.fieldInline}>
              <input
                type="checkbox"
                checked={draft.permissions.includes(permission)}
                onChange={(event) => togglePermission(permission, event.currentTarget.checked)}
              />
              <span className={styles.permission}>{permission}</span>
              <span className={styles.hint}>{API_KEY_PERMISSION_DESCRIPTIONS[permission]}</span>
            </label>
          ))}
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{"IPs autorisées (une par ligne, vide = toutes) — 1.2.3.4, 1.2.3.0/24, 1.2.3.*" /* TRANSLATION */}</span>
          <textarea
            className={styles.textarea}
            value={draft.filterIps}
            spellCheck={false}
            onChange={(event) => setDraftField("filterIps", event.currentTarget.value)}
          />
        </label>

        <div className={styles.editorButtons}>
          <button type="button" className={styles.actionButton} disabled={busy} onClick={save}>
            {draft.id ? "Enregistrer" : "Créer" /* TRANSLATION */}
          </button>
          <button type="button" className={styles.actionButton} disabled={busy} onClick={() => setDraft(undefined)}>
            {"Annuler" /* TRANSLATION */}
          </button>
        </div>
      </div>
    );
  };

  const renderStatus = (apiKey: ApiKey) => {
    if (apiKey.revokedAt) return <span className={styles.badgeRevoked}>{"révoquée" /* TRANSLATION */}</span>;
    if (isExpired(apiKey)) return <span className={styles.badgeExpired}>{"expirée" /* TRANSLATION */}</span>;
    return <span className={styles.badgeActive}>{"active" /* TRANSLATION */}</span>;
  };

  const renderApiKey = (apiKey: ApiKey) => {
    const editing = draft?.id === apiKey._id;
    const rateLimit = apiKey.rateLimitPerMinute === null ? "illimité" : `${apiKey.rateLimitPerMinute ?? API_KEY_DEFAULT_RATE_LIMIT_PER_MINUTE} / min`;
    return (
      <div key={apiKey._id} className={[styles.keyBlock, apiKey.revokedAt ? styles.keyBlockRevoked : ""].join(" ")}>
        <div className={styles.keyRow}>
          <span className={styles.keyName}>{apiKey.name}</span>
          <span className={styles.badges}>
            {renderStatus(apiKey)}
            {apiKey.permissions?.map((permission) => (
              <span key={permission} className={styles.badgePermission}>
                {permission}
              </span>
            ))}
          </span>
          <span className={styles.keyActions}>
            {!apiKey.revokedAt && !editing ? (
              <button type="button" className={styles.actionButton} disabled={busy} onClick={() => setDraft(draftFromApiKey(apiKey))}>
                {"Modifier" /* TRANSLATION */}
              </button>
            ) : null}
            {!apiKey.revokedAt ? (
              <button type="button" className={styles.actionButtonDanger} disabled={busy} onClick={() => confirmRevoke(apiKey)}>
                {"Révoquer" /* TRANSLATION */}
              </button>
            ) : null}
          </span>
        </div>
        <div className={styles.details}>
          <span>{`Id : ${apiKey._id}`}</span>
          <span>{`Créée : ${formatDate(apiKey.createdAt)}`}</span>
          <span>{`Expire : ${apiKey.expiresAt ? formatDate(apiKey.expiresAt) : "jamais"}`}</span>
          <span>{`Dernière utilisation : ${formatDate(apiKey.lastUsedAt)}${apiKey.lastUsedIp ? ` (${apiKey.lastUsedIp})` : ""}`}</span>
          <span>{`Limite : ${rateLimit}`}</span>
          <span>{`IPs : ${apiKey.filterIps?.length ? apiKey.filterIps.join(", ") : "toutes"}`}</span>
          {apiKey.revokedAt ? <span>{`Révoquée : ${formatDate(apiKey.revokedAt)}`}</span> : null}
        </div>
        {editing ? renderEditor() : null}
      </div>
    );
  };

  const revokedCount = apiKeys.filter((apiKey) => apiKey.revokedAt).length;
  const visibleApiKeys = showRevoked ? apiKeys : apiKeys.filter((apiKey) => !apiKey.revokedAt);
  const creating = !!draft && !draft.id;

  return (
    <PageContainer title={"API keys" /* TRANSLATION */} loading={!loaded}>
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <button type="button" className={styles.actionButton} disabled={busy || creating} onClick={() => setDraft(emptyDraft())}>
            {"+ Nouvelle API key" /* TRANSLATION */}
          </button>
          {revokedCount ? (
            <label className={styles.fieldInline}>
              <input type="checkbox" checked={showRevoked} onChange={(event) => setShowRevoked(event.currentTarget.checked)} />
              {`Afficher les clés révoquées (${revokedCount})` /* TRANSLATION */}
            </label>
          ) : null}
        </div>

        {creating ? <div className={styles.keyBlock}>{renderEditor()}</div> : null}

        {visibleApiKeys.length ? (
          visibleApiKeys.map(renderApiKey)
        ) : loaded && !creating ? (
          <div className={styles.empty}>{"Aucune API key." /* TRANSLATION */}</div>
        ) : null}
      </div>
    </PageContainer>
  );
};

export default ApiKeysPage;
